import React, { useState } from 'react';
import { Shield, KeyRound, RefreshCcw, AlertCircle, CheckCircle2, Loader2, Copy, Check } from 'lucide-react';
import { adminApi, useAdminAuth } from './AdminAuthContext';

const PWD_MIN = 10;

function strength(pwd) {
  let s = 0;
  if (pwd.length >= PWD_MIN) s++;
  if (pwd.length >= 14) s++;
  if (/[A-Z]/.test(pwd)) s++;
  if (/[0-9]/.test(pwd)) s++;
  if (/[^A-Za-z0-9]/.test(pwd)) s++;
  return Math.min(s, 4);
}
const COLORS = ['#C03A1A', '#E07A2A', '#E0A930', '#9CCB3B', '#7FC242'];
const LABELS = ['zu schwach', 'schwach', 'okay', 'stark', 'exzellent'];


function PasswordChangeForm() {
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [next2, setNext2] = useState('');
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault(); setError(null); setDone(false);
    if (next.length < PWD_MIN) { setError(`Neues Passwort min. ${PWD_MIN} Zeichen.`); return; }
    if (next !== next2) { setError('Neue Passwörter stimmen nicht überein.'); return; }
    if (strength(next) < 2) { setError('Neues Passwort zu schwach.'); return; }
    if (otp.length !== 6) { setError('6-Code aus Authenticator-App eingeben.'); return; }
    setBusy(true);
    try {
      await adminApi.post('/admin/password/change', { current_password: cur, new_password: next, totp_code: otp });
      setCur(''); setNext(''); setNext2(''); setOtp('');
      setDone(true); setTimeout(() => setDone(false), 4000);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Passwort-Änderung fehlgeschlagen.');
    } finally { setBusy(false); }
  };
  const s = strength(next);

  return (
    <form onSubmit={onSubmit} data-testid="admin-pwd-change-form" className="bg-white rounded-2xl border border-[#E5E0D2] p-6 space-y-4">
      <div className="flex items-center gap-3 pb-2 border-b border-[#E5E0D2]">
        <KeyRound className="w-4 h-4 text-[#F26B1A]" />
        <p className="font-display text-[18px] text-[#1F1D1A]">Passwort ändern</p>
      </div>
      <label className="block">
        <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Aktuelles Passwort</span>
        <input type="password" required autoComplete="current-password" value={cur}
          onChange={(e) => setCur(e.target.value)} data-testid="admin-pwd-current"
          className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px]" />
      </label>
      <label className="block">
        <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Neues Passwort</span>
        <input type="password" required autoComplete="new-password" value={next}
          onChange={(e) => setNext(e.target.value)} data-testid="admin-pwd-new"
          className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px]" />
        {next.length > 0 && (
          <div className="mt-2 flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-[#E5E0D2] rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${(s / 4) * 100}%`, backgroundColor: COLORS[s] }} />
            </div>
            <span className="text-[11px] tabular-nums" style={{ color: COLORS[s] }}>{LABELS[s]}</span>
          </div>
        )}
      </label>
      <label className="block">
        <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Neues Passwort wiederholen</span>
        <input type="password" required autoComplete="new-password" value={next2}
          onChange={(e) => setNext2(e.target.value)} data-testid="admin-pwd-new2"
          className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px]" />
      </label>
      <label className="block">
        <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">6-Code aus Authenticator-App</span>
        <input type="text" inputMode="numeric" pattern="\d{6}" maxLength={6} autoComplete="one-time-code"
          value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
          data-testid="admin-pwd-otp"
          className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[18px] font-mono text-center tabular-nums tracking-[0.3em]" />
      </label>
      {error && <p data-testid="admin-pwd-err" className="inline-flex items-start gap-2 text-[12.5px] text-[#C03A1A]"><AlertCircle className="w-4 h-4 mt-0.5" /> {error}</p>}
      {done && <p data-testid="admin-pwd-ok" className="inline-flex items-center gap-2 text-[12.5px] text-[#7FC242]"><CheckCircle2 className="w-4 h-4" /> Passwort wurde geändert.</p>}
      <button type="submit" disabled={busy} data-testid="admin-pwd-submit"
        className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#F26B1A] text-white text-[13px] font-semibold hover:bg-[#D85A0A] disabled:opacity-50 transition-colors">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
        Passwort ändern
      </button>
    </form>
  );
}


function RecoveryRegenForm({ remaining, onRegen }) {
  const [cur, setCur] = useState('');
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [codes, setCodes] = useState(null);
  const [copied, setCopied] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault(); setError(null);
    if (otp.length !== 6) { setError('6-Code eingeben.'); return; }
    if (!window.confirm('Sicher? Die alten Recovery-Codes werden ungültig.')) return;
    setBusy(true);
    try {
      const { data } = await adminApi.post('/admin/recovery/regenerate', { current_password: cur, totp_code: otp });
      setCodes(data.recovery_codes); setCur(''); setOtp('');
      onRegen?.(data.recovery_codes.length);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Fehlgeschlagen.');
    } finally { setBusy(false); }
  };

  const copyAll = async () => {
    if (!codes) return;
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[admin-security] clipboard write failed:', err);
      }
    }
  };

  return (
    <form onSubmit={onSubmit} data-testid="admin-recovery-form" className="bg-white rounded-2xl border border-[#E5E0D2] p-6 space-y-4">
      <div className="flex items-center justify-between pb-2 border-b border-[#E5E0D2]">
        <div className="flex items-center gap-3">
          <RefreshCcw className="w-4 h-4 text-[#F26B1A]" />
          <p className="font-display text-[18px] text-[#1F1D1A]">Recovery-Codes</p>
        </div>
        <span data-testid="admin-recovery-remaining"
          className={`text-[12px] font-semibold tabular-nums px-2.5 py-1 rounded-full ${
            remaining <= 2 ? 'bg-[#C03A1A]/10 text-[#C03A1A]'
              : remaining <= 4 ? 'bg-[#E0A930]/15 text-[#8A6510]'
                : 'bg-[#7FC242]/15 text-[#3D5F1F]'
          }`}>
          {remaining} verbleibend
        </span>
      </div>

      {codes ? (
        <div data-testid="admin-recovery-result">
          <p className="text-[13px] text-[#1F1D1A] mb-3">
            <strong>Neue Recovery-Codes — werden nur jetzt einmalig angezeigt:</strong>
          </p>
          <ul className="grid grid-cols-2 gap-2 p-3 bg-[#FAF8F2] rounded-lg border border-[#E5E0D2] mb-3">
            {codes.map((c) => <li key={c} className="font-mono text-[13.5px] tabular-nums">{c}</li>)}
          </ul>
          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" onClick={copyAll} data-testid="admin-recovery-copy"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${
                copied ? 'bg-[#7FC242] text-white' : 'bg-white border border-[#E5E0D2] hover:border-[#F26B1A]'
              }`}>
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Kopiert' : 'Alle kopieren'}
            </button>
            <button type="button" onClick={() => setCodes(null)} className="text-[12px] text-[#5C5852] underline">Schließen</button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-[12.5px] text-[#5C5852]">
            Generiere 8 neue Codes (ersetzt alle bestehenden). Empfohlen, wenn weniger als 3 Codes übrig sind oder du sie nicht mehr findest.
          </p>
          <label className="block">
            <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Aktuelles Passwort</span>
            <input type="password" required autoComplete="current-password" value={cur}
              onChange={(e) => setCur(e.target.value)} data-testid="admin-recovery-pwd"
              className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px]" />
          </label>
          <label className="block">
            <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">6-Code aus Authenticator-App</span>
            <input type="text" inputMode="numeric" pattern="\d{6}" maxLength={6} autoComplete="one-time-code"
              value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              data-testid="admin-recovery-otp"
              className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[18px] font-mono text-center tabular-nums tracking-[0.3em]" />
          </label>
          {error && <p data-testid="admin-recovery-err" className="inline-flex items-start gap-2 text-[12.5px] text-[#C03A1A]"><AlertCircle className="w-4 h-4 mt-0.5" /> {error}</p>}
          <button type="submit" disabled={busy} data-testid="admin-recovery-submit"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#F26B1A] text-white text-[13px] font-semibold hover:bg-[#D85A0A] disabled:opacity-50 transition-colors">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            Neue Codes generieren
          </button>
        </>
      )}
    </form>
  );
}


export default function AdminSettingsSecurityPage() {
  const { admin, refresh } = useAdminAuth();
  return (
    <div data-testid="admin-settings-security-page" className="w-full max-w-none space-y-6">
      <div className="flex items-center gap-3">
        <span className="inline-flex w-10 h-10 rounded-full bg-[#F26B1A]/10 text-[#F26B1A] items-center justify-center">
          <Shield className="w-5 h-5" />
        </span>
        <h1 className="font-display text-[22px] sm:text-[24px] lg:text-[28px] text-[#1F1D1A] leading-none">Sicherheit</h1>
      </div>

      <div className="bg-white rounded-2xl border border-[#E5E0D2] p-6 grid grid-cols-1 md:grid-cols-3 gap-3 text-[13.5px]">
        <p><strong className="text-[#1F1D1A]">E-Mail:</strong> <span data-testid="admin-security-email">{admin?.email}</span></p>
        <p><strong className="text-[#1F1D1A]">2-Faktor-Authentifizierung:</strong>{' '}
          <span data-testid="admin-2fa-state" className={admin?.totp_enabled ? 'text-[#7FC242] font-semibold' : 'text-[#C03A1A] font-semibold'}>
            {admin?.totp_enabled ? 'aktiv' : 'inaktiv'}
          </span>
        </p>
        {admin?.activated_at && <p><strong className="text-[#1F1D1A]">Aktiviert am:</strong> {new Date(admin.activated_at).toLocaleString('de-DE')}</p>}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        <PasswordChangeForm />
        <RecoveryRegenForm
          remaining={admin?.recovery_codes_remaining ?? 0}
          onRegen={() => refresh()}
        />
      </div>
    </div>
  );
}
